'use client'

import { useEffect, useState } from 'react'

import { IconChevDown } from '@/app/components/atoms/Icons'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { useLocationHash } from '@/app/lib/hooks/useLocationHash'
import { NO_APLICA } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { cx } from '@/app/lib/utils'

import {
  CHEVRON_ICON_SIZE,
  PANEL_ID_PREFIX,
  TRIGGER_ID_PREFIX,
} from './Accordion.constants'
import styles from './Accordion.module.scss'
import type { AccordionItem, AccordionProps } from './Accordion.types'

/**
 * Accordion [atom] — DS Entradas.
 *
 * Lista de paneles colapsables. Cada cabecera es un `<button>` que muestra u
 * oculta su panel asociado. Por defecto, abrir un panel cierra el resto
 * (`allowMultiple={false}`, comportamiento de la fuente); con `allowMultiple`
 * pueden coexistir varios abiertos.
 *
 * Client Component: mantiene el estado de apertura y gestiona el evento de clic.
 *
 * Accesibilidad (WCAG 2.1 AA — 1.3.1, 2.1.1, 2.4.7, 4.1.2):
 * - Cada cabecera es un `<button aria-expanded aria-controls>` (operable por
 *   teclado de forma nativa) que apunta a su panel.
 * - Con `headingLevel`, cada disparador se envuelve en el heading real
 *   correspondiente (`<h2>`…`<h6>`, patrón APG de disclosure), para que la
 *   pregunta forme parte de la estructura de encabezados de la página
 *   (1.3.1) — pensado para acordeones tipo FAQ con relevancia SEO. Sin ella,
 *   el `<button>` se pinta suelto, igual que siempre.
 * - El panel es una `role="region"` etiquetada por su cabecera
 *   (`aria-labelledby`). Colapsado, lleva el atributo `inert` — lo saca del
 *   árbol de accesibilidad y de la tabulación sin `display: none`, a
 *   diferencia de `hidden`, precisamente para poder animar la transición de
 *   apertura/cierre con CSS (`grid-template-rows`, ver `Accordion.module.scss`).
 * - Foco visible vía mixin `focus-ring`; el chevron rota al expandir.
 * - Respeta `prefers-reduced-motion` (sin transiciones).
 *
 * Anclas: un panel con `anchorId` se abre al llegar (o navegar) a
 * `#anchorId`, y la página se desplaza hasta él — p. ej. los enlaces del
 * footer a `/entradas/faqs/#como-puedo-comprar`.
 *
 * Analítica (`Entradas + FAQs.csv` → "acordeon — pagina faqs", módulo
 * transversal): con `analytics` presente, cada apertura/cierre dispara un
 * `ui_interaction` (`component_type=accordion`). Sin ella (resto de
 * consumidores, p. ej. `Biografia`) no cambia nada de lo existente.
 */
export function Accordion({
  items,
  allowMultiple = false,
  defaultOpenIds = [],
  className,
  style,
  analytics: analyticsConfig,
  headingLevel,
}: AccordionProps) {
  const analytics = useOptionalPageAnalytics()
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(
    () => new Set(defaultOpenIds),
  )

  // Abre el panel al que apunta el `#hash` de la URL. Se ajusta el estado
  // durante el render (no en un efecto) al detectar un hash nuevo, el patrón
  // recomendado por React para derivar estado de un valor externo.
  const hash = useLocationHash()
  const [handledHash, setHandledHash] = useState('')
  const hashTarget = hash ? items.find((item) => item.anchorId === hash) : undefined
  if (hash !== handledHash) {
    setHandledHash(hash)
    if (hashTarget) {
      setOpenIds((prev) =>
        allowMultiple ? new Set(prev).add(hashTarget.id) : new Set([hashTarget.id]),
      )
    }
  }

  // Tras abrirlo, lleva el panel a la vista: el scroll nativo al `#id` ocurre
  // antes de hidratar o con el panel aún cerrado.
  const hashTargetAnchor = hashTarget?.anchorId
  useEffect(() => {
    if (!hashTargetAnchor) return
    document.getElementById(hashTargetAnchor)?.scrollIntoView({ block: 'start' })
  }, [hashTargetAnchor])

  function toggle(item: AccordionItem) {
    const willOpen = !openIds.has(item.id)

    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(item.id)) {
        next.delete(item.id)
      } else {
        if (!allowMultiple) next.clear()
        next.add(item.id)
      }
      return next
    })

    if (!analyticsConfig) return
    analytics?.trackUiInteraction({
      component_type: 'accordion',
      component_action: willOpen ? 'expand' : 'collapse',
      component_id: analyticsConfig.componentIdPrefix
        ? `${analyticsConfig.componentIdPrefix}${slugify(item.id).replace(/-/g, '_')}`
        : item.id,
      component_label: slugify(item.analyticsLabel ?? item.id),
      selected_value: NO_APLICA,
      module_name: analyticsConfig.moduleName,
      module_type: analyticsConfig.moduleType,
      module_position: analyticsConfig.modulePosition,
    })
  }

  if (items.length === 0) return null

  // Nombre de elemento JSX dinámico: patrón válido en React/TS (la variable
  // debe empezar en mayúscula para que JSX la trate como componente/elemento
  // en vez de como una etiqueta literal). El tipo queda acotado por la unión
  // literal `2 | 3 | 4 | 5 | 6` de `headingLevel`, sin necesidad de `as any`.
  const Heading = headingLevel ? (`h${headingLevel}` as const) : null

  return (
    <div className={cx(styles.root, className)} style={style}>
      {items.map((item) => {
        const isOpen = openIds.has(item.id)
        const triggerId = `${TRIGGER_ID_PREFIX}${item.id}`
        const panelId = `${PANEL_ID_PREFIX}${item.id}`

        const trigger = (
          <button
            id={triggerId}
            type="button"
            className={styles.header}
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => toggle(item)}
          >
            <span className={styles.title}>{item.title}</span>
            <span
              className={cx(styles.chevron, isOpen && styles.chevronOpen)}
              aria-hidden="true"
            >
              <IconChevDown size={CHEVRON_ICON_SIZE} />
            </span>
          </button>
        )

        return (
          <div key={item.id} id={item.anchorId} className={styles.item}>
            {Heading ? <Heading>{trigger}</Heading> : trigger}
            <div
              id={panelId}
              role="region"
              aria-labelledby={triggerId}
              className={cx(styles.panel, isOpen && styles.panelOpen)}
              inert={!isOpen}
            >
              <div className={styles.panelInner}>{item.content}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
